import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';

import { useNavigate } from 'react-router';

import styled from '@emotion/styled';

import { ClientError } from '../../api';
import { BubbleSVG } from '../../components/BubbleSVG';
import { Button } from '../../components/Button';
import { Fields } from '../../components/Fields';
import { Loader } from '../../components/Loader';
import { Success } from '../../components/Success';
import { VisibleOnVersion } from '../../components/VisibleOnVersion';
import {
    useFormFields,
    useProfileData,
    useStoryblokPath,
    useStoryblokStrings,
} from '../../hooks';
import {
    ApiContext,
    CoreContext,
    Firebase,
    FirebaseContext,
} from '../../providers';
import { classNames } from '../../services/classNames';
import { dynamicTemplate } from '../../services/dynamicTemplate';
import { media } from '../../services/media';
import { setError } from '../../state';
import { Storyblok } from '../../storyblok';

const emailCodes = [
    'auth/invalid-email',
    'auth/invalid-email-verified',
    'auth/user-not-found',
    'auth/email-already-in-use',
];

const passwordCodes = ['auth/wrong-password', 'auth/weak-password'];

/**
 * @typedef {"login" | "register" | "resetPassword"} FormState
 */

interface AltinnConfirmationProps {
    sectionData: {
        altinnConfirmationFields: any;
        altinnConfirmationRegisterFields: any;
        altinnConfirmationResetFields: any;
        backgroundImage: Storyblok.Field.Asset;
        headingExistingUser: string;
        subheadingExistingUser: string;
        heading: string;
        subheading: string;
    };
}

export const Confirmation: React.FC<AltinnConfirmationProps> = ({
    sectionData,
}) => {
    const { settings } = useContext(CoreContext);

    const {
        altinnConfirmationFields,
        altinnConfirmationRegisterFields,
        altinnConfirmationResetFields,
        backgroundImage,
        headingExistingUser,
        subheadingExistingUser,
        heading,
        subheading,
    } = sectionData;

    const { redirectPathMyScore } = useStoryblokPath();

    const { store } = useContext(CoreContext);
    const { userSignOut, auth, firebaseFormSubmit } =
        useContext(FirebaseContext);
    const { Upscore } = useContext(ApiContext);
    const [isLoading, setIsLoading] = useState(false);

    /** @type {[FormState, React.Dispatch<React.SetStateAction<FormState>>]} */
    const [formType, setFormType] =
        useState<
            Exclude<Firebase.FormSubmitType, 'firebase_reset_confirmation'>
        >('firebase_login');

    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    /** @type {[string, React.Dispatch<React.SetStateAction<string>>]} */

    const scrollWrapperRef = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();

    const formStrings = useStoryblokStrings('form-strings');
    const generalStrings = useStoryblokStrings('general-strings');

    const loginButton = formStrings.log_in || '';
    const seeScoreButton = formStrings.see_score || '';

    const { profileData } = useProfileData();

    const allFormFields = {
        firebase_login: altinnConfirmationFields,
        firebase_register: altinnConfirmationRegisterFields,
        firebase_reset: altinnConfirmationResetFields,
    } as {
        [key in Exclude<
            Firebase.FormSubmitType,
            'firebase_reset_confirmation'
        >]: any;
    };

    const buttonVariants = {
        upscore: 'secondary',
        formue: 'formue-boxed',
        pengestyrke: 'boxed-ghost',
    } as any;

    /** @type Array */
    const formFields =
        allFormFields[formType] || allFormFields.firebase_register;

    const mapFieldsValues = useCallback((data) => {
        if (!data) {
            return {};
        }

        const { email: email, phoneNumber } = data;

        return {
            email: email || '',
            /**
             * Remove white spaces and return only last 8 chars to get just the phone number (without country code)
             */
            phoneNumber: phoneNumber
                ? phoneNumber.replace(/[^\d]/g, '').slice(-8)
                : '',
        };
    }, []);

    const [fieldErrors, setFieldErrors, resetFieldErrors] =
        useFormFields(formFields);
    const [fieldValues, setFieldValues] = useFormFields(
        formFields,
        mapFieldsValues(profileData),
    );

    const handleInnerLinkClick = useCallback(
        (e) => {
            const targetElement = e.target;
            if (targetElement.tagName !== 'SPAN') {
                return;
            }

            e.preventDefault();

            const action = targetElement.className;
            if (action === 'resetPassword') {
                setFormType('firebase_reset');
            }
        },
        [setFormType],
    );

    const backToLoginHandler = useCallback(() => {
        setFormType('firebase_login');
        setSuccessMessage(null);
    }, []);

    const formSubmitHandler = useCallback(
        async (e) => {
            e.preventDefault();
            resetFieldErrors();
            const {
                phoneNumber = null,
                password = null,
                email = null,
            } = fieldValues;

            // TODO: Fix type
            let fieldsToValidate = { email } as any;

            if (formType === 'firebase_register') {
                fieldsToValidate = {
                    phoneNumber,
                    password,
                    email,
                };
            }

            setIsLoading(true);
            try {
                await firebaseFormSubmit({
                    type: formType,
                    data: {
                        ...fieldValues,
                    },
                });

                if (formType === 'firebase_reset') {
                    setSuccessMessage(generalStrings.firebase_reset_password);

                    setTimeout(() => {
                        setFormType('firebase_login');
                        setSuccessMessage(null);
                    }, 4000);
                }

                if (formType !== 'firebase_reset') {
                    const activateResponse = await Upscore.postUserActivate({
                        altinnData: JSON.stringify(profileData),
                        marketingConsent: fieldValues.consent || false,
                    }).catch((error) => {
                        userSignOut();
                        throw new ClientError('Upscore /user/activate failed', {
                            category: 'Upscore API',
                            type: 'activate-error',
                            error,
                            extras: {
                                error: JSON.stringify(error, null, 2),
                            },
                        });
                    });

                    if (activateResponse.result === true) {
                        redirectPathMyScore();
                    }
                } else {
                    setIsLoading(false);
                }
            } catch (error: any) {
                console.error(error);

                if (error instanceof ClientError) {
                    store.dispatch(setError('activate-error'));
                } else {
                    const { code, message } = error;

                    if (emailCodes.includes(code)) {
                        setFieldErrors({
                            email: message,
                        });
                    } else if (passwordCodes.includes(code)) {
                        setFieldErrors({
                            password: message,
                        });
                    } else {
                        console.error(message);
                        store.dispatch(setError('general'));
                    }
                }

                setIsLoading(false);
            }
        },
        [
            resetFieldErrors,
            fieldValues,
            formType,
            setFieldErrors,
            generalStrings,
            profileData,
            userSignOut,
            navigate,
        ],
    );

    useEffect(() => {
        if (!profileData) {
            navigate('/altinn-login');
            return;
        }

        const effectHandler = async () => {
            setIsLoading(true);
            const { email } = fieldValues;
            const response = await auth.fetchSignInMethodsForEmail(email);

            if (Array.isArray(response) && response.length) {
                setFormType('firebase_login');
            } else {
                setFormType('firebase_register');
            }

            setIsLoading(false);
        };
        effectHandler();

        if (scrollWrapperRef.current) {
            // disableBodyScroll(scrollWrapperRef.current);
        }

        // eslint-disable-next-line consistent-return
        return () => {
            // clearAllBodyScrollLocks();
        };
    }, []);

    const profileDataName = profileData.name
        .toLowerCase()
        .split(' ')
        .map((s: string) => s.charAt(0).toUpperCase() + s.substring(1))
        .join(' ');

    const globalTemplateVariables = { name: profileDataName };
    const existingUserHeader = dynamicTemplate(
        headingExistingUser,
        globalTemplateVariables,
    );
    const regex = /\[(register|login|resetPassword)\]([a-zA-Z ]+)\[\/\w*\]/gm;
    const replaceString = '<span class="$1">$2</span>';
    const existingUserSubHeader = subheadingExistingUser.replace(
        regex,
        replaceString,
    );

    const headerTitle = {
        firebase_login: existingUserHeader,
        firebase_register: heading,
        firebase_reset: generalStrings.reset_password_title,
    };
    const subHeaderText = {
        firebase_login: existingUserSubHeader,
        firebase_register: subheading,
        firebase_reset: generalStrings.reset_password_description,
    };
    const buttonTitle = {
        firebase_login: loginButton,
        firebase_register: seeScoreButton,
        firebase_reset: formStrings.reset_password,
    };

    return (
        <div className="BankLoginScene__content-wrapper">
            <VisibleOnVersion versions={['upscore', 'pengestyrke']}>
                {headerTitle[formType] && (
                    <Heading>{headerTitle[formType]}</Heading>
                )}

                {subHeaderText[formType] && (
                    <p
                        className="BankLoginScene__subTitle"
                        onClick={handleInnerLinkClick}
                        dangerouslySetInnerHTML={{
                            __html: subHeaderText[formType],
                        }}
                    />
                )}
            </VisibleOnVersion>

            <div className="BankLoginScene__bubble">
                <VisibleOnVersion versions={['upscore', 'pengestyrke']}>
                    <BubbleSVG
                        backgroundSvgName="bankLogin"
                        centerContentVertically
                    >
                        {isLoading && (
                            <Loader centered backgroundColor="#ffffff" />
                        )}

                        {!isLoading &&
                            successMessage &&
                            formStrings.continue_button && (
                                <>
                                    <p>{successMessage}</p>
                                    <Button
                                        variant={
                                            buttonVariants[
                                                settings.languageVersion
                                            ]
                                        }
                                        className="BankLoginForm__button"
                                        type="button"
                                        onClick={backToLoginHandler}
                                    >
                                        {formStrings.continue_button}
                                    </Button>
                                </>
                            )}

                        {!isLoading && !successMessage && (
                            <form
                                className={classNames(
                                    'LoginForm',
                                    'BankLoginForm',
                                    `BankLoginForm--altinnConfirmation`,
                                )}
                                onSubmit={formSubmitHandler}
                            >
                                <Fields
                                    fieldValues={fieldValues}
                                    fields={formFields}
                                    setFieldValues={setFieldValues}
                                    fieldErrors={fieldErrors}
                                    setFieldErrors={setFieldErrors}
                                    inputType="formField"
                                />

                                <Button
                                    variant={
                                        buttonVariants[settings.languageVersion]
                                    }
                                    className="BankLoginForm__button"
                                    type="submit"
                                    data-event={`altinn login confirmation (${
                                        formType === 'firebase_reset'
                                            ? 'reset'
                                            : 'login'
                                    } button)`}
                                >
                                    {buttonTitle[formType] ? (
                                        buttonTitle[formType]
                                    ) : (
                                        <Loader centered />
                                    )}
                                </Button>

                                {formType === 'firebase_reset' && (
                                    <p
                                        className="BankLoginForm__back-button"
                                        onClick={backToLoginHandler}
                                    >
                                        {formStrings.cancel_button}
                                    </p>
                                )}
                            </form>
                        )}
                    </BubbleSVG>
                </VisibleOnVersion>

                <VisibleOnVersion versions={['formue']}>
                    <Success />
                </VisibleOnVersion>
            </div>
        </div>
    );
};

const Heading = styled.h2`
    text-align: center;
    font-weight: 700;

    ${media.min('md')} {
        max-width: 550px;
        margin: 0 auto 1em;
    }
`;
